import { useState } from 'react';
import { changePassword, ApiError } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// CHOOSE YOUR OWN PASSWORD BEFORE YOU GO ANY FURTHER.
//
// Three accounts on this deployment were created by somebody else typing a
// password for them and passing it on — Jaxen's, Allow Digital's, and the
// baseline test account. Those passwords were shown once in a chat transcript
// in August and every one of them still worked. A password transmitted over a
// channel neither party controls is a shared secret, not a credential.
//
// THIS SCREEN IS A WALL, NOT A PROMPT. It renders instead of the app, above the
// router, so there is no nav to click past and no URL to type around it. A
// dismissible banner would be ignored by exactly the person who most needs to
// act on it, and the flag would then live forever in the database describing a
// risk nobody ever closed.
//
// It cannot be escaped by reloading either: `must_change_password` rides on
// every identity response, /api/auth/me included, so a fresh page load lands
// straight back here.
// ─────────────────────────────────────────────────────────────────────────────

export function ChangePassword({ email, onDone }: {
  email: string;
  onDone: (token: string) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Checked here as well as on the server: retyping is a defence against a
    // typo locking somebody out of an account they just proved they own, and
    // the server has no second copy to compare against.
    if (next !== again) { setError('Those two do not match.'); return; }
    setBusy(true);
    try {
      const r = await changePassword(current, next);
      onDone(r.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      <form className="gatecard" onSubmit={(e) => void submit(e)}>
        <h1>Choose your own password</h1>
        <p className="fieldhint">
          The password you signed in with was set for you and sent to you. Pick
          one only you know. Every other sign-in on this account ends when you do.
        </p>
        <p className="fieldhint"><strong>{email}</strong></p>

        <label className="field">
          <span className="field-label">The password you were given</span>
          <input type="password" autoComplete="current-password" required
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Your new password</span>
          <input type="password" autoComplete="new-password" required
            value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Type it again</span>
          <input type="password" autoComplete="new-password" required
            value={again} onChange={(e) => setAgain(e.target.value)} />
        </label>

        {error && <p className="err">{error}</p>}

        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set my password'}
        </button>
      </form>
    </div>
  );
}
